import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PAGE_SIZE_OPTIONS, totalPages } from "@/lib/list-query";

interface DataPaginationProps {
  page: number;
  size: number;
  total: number;
  onPageChange: (page: number) => void;
  onSizeChange: (size: number) => void;
  /** Plural noun for the count, e.g. "agents". */
  label?: string;
}

/**
 * The pager for every paged screen. Reads `total` from the server rather than
 * the rows on screen, so "1-10 of 148" is the real count.
 */
export function DataPagination({
  page,
  size,
  total,
  onPageChange,
  onSizeChange,
  label = "results",
}: DataPaginationProps) {
  const pages = totalPages(total, size);
  const first = total === 0 ? 0 : (page - 1) * size + 1;
  const last = Math.min(page * size, total);
  // A screen may open at a size of its own — the tools panel starts at 20 — and
  // the select renders blank for a value it has no item for.
  const sizes = PAGE_SIZE_OPTIONS.includes(size)
    ? PAGE_SIZE_OPTIONS
    : [...PAGE_SIZE_OPTIONS, size].sort((a, b) => a - b);

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 pt-6">
      <p className="text-sm text-muted-foreground" aria-live="polite">
        {total === 0 ? `No ${label}` : `${first}–${last} of ${total} ${label}`}
      </p>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Rows</span>
          <Select value={String(size)} onValueChange={(value) => onSizeChange(Number(value))}>
            <SelectTrigger className="w-[72px] h-9" aria-label="Rows per page">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sizes.map((option) => (
                <SelectItem key={option} value={String(option)}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm tabular-nums">
            Page {page} of {pages}
          </span>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= pages}
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
